import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { Zap, Target, Shield, BarChart3, Sparkles, Globe, Send, Bot, Loader2, CheckCircle2, ChevronRight, Menu, X, Search, User, LogOut } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DSP_OPTIONS = [
  "DV360", "The Trade Desk", "Yahoo", "Quantcast", "Adform", 
  "Adobe", "Amazon", "Eskimi", "Hawk", "Crimtan", 
  "Loopme", "Nexxen", "Sqreem", "Stackadapt"
];

const MEDIA_TYPES = ["Display", "Video", "Native", "Audio"];
const DEVICES = ["Desktop", "Mobile", "Tablet", "CTV"];
const ENVIRONMENTS = ["Web", "App"];

const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan",
  "Bahrain", "Bangladesh", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana",
  "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Cambodia", "Cameroon", "Canada", "Chile", "China", "Colombia",
  "Costa Rica", "Croatia", "Cyprus", "Czech Republic", "Denmark", "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Estonia",
  "Ethiopia", "Finland", "France", "Georgia", "Germany", "Ghana", "Greece", "Guatemala", "Honduras", "Hong Kong",
  "Hungary", "Iceland", "India", "Indonesia", "Iraq", "Ireland", "Israel", "Italy", "Ivory Coast", "Jamaica",
  "Japan", "Jordan", "Kazakhstan", "Kenya", "Kuwait", "Latvia", "Lebanon", "Libya", "Lithuania", "Luxembourg",
  "Macau", "Malaysia", "Maldives", "Malta", "Mauritius", "Mexico", "Moldova", "Monaco", "Mongolia", "Montenegro",
  "Morocco", "Mozambique", "Myanmar", "Namibia", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Nigeria", "North Macedonia",
  "Norway", "Oman", "Pakistan", "Panama", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar",
  "Romania", "Russia", "Rwanda", "Saudi Arabia", "Senegal", "Serbia", "Singapore", "Slovakia", "Slovenia", "South Africa",
  "South Korea", "Spain", "Sri Lanka", "Sweden", "Switzerland", "Taiwan", "Tanzania", "Thailand", "Tunisia", "Turkey",
  "UAE", "Uganda", "Ukraine", "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Venezuela", "Vietnam", "Zambia", "Zimbabwe"
];

const INGENIUM_LOGO = "https://customer-assets.emergentagent.com/job_agentic-curation/artifacts/zri9wsj7_ingenium-logo.png";

const Navbar = ({ onCreateDeal, onLogin, isLoggedIn, userName, userRole, onLogout, onDashboard }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const getDashboardLabel = () => {
    if (userRole === 'client') return 'My Deals';
    return 'Dashboard';
  };

  return (
    <nav className="navbar fixed top-0 left-0 right-0 z-50 px-6 py-4">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <a href="/" className="flex items-center">
          <img 
            src={INGENIUM_LOGO} 
            alt="Ingenium Digital" 
            className="h-8 md:h-10"
          />
        </a>
        
        <div className="hidden md:flex items-center gap-6">
          <a href="#features" className="text-[#ebebeb] hover:text-white transition-colors text-sm font-mono">Features</a>
          <a href="#how-it-works" className="text-[#ebebeb] hover:text-white transition-colors text-sm font-mono">How It Works</a>
          <a href="#inventory" className="text-[#ebebeb] hover:text-white transition-colors text-sm font-mono">Inventory</a>
          <button 
            onClick={onCreateDeal}
            className="ingenium-button"
            data-testid="navbar-create-deal-btn"
          >
            <span>Create a Deal</span>
          </button>
          
          {isLoggedIn ? (
            <div className="flex items-center gap-3">
              <button
                onClick={onDashboard}
                className="flex items-center gap-2 text-[#ebebeb] hover:text-white transition-colors text-sm font-mono"
                data-testid="navbar-dashboard-btn"
              >
                <User className="w-4 h-4" />
                {getDashboardLabel()}
              </button>
              <button
                onClick={onLogout}
                className="flex items-center gap-2 text-[#ebebeb]/60 hover:text-white transition-colors text-sm font-mono"
                data-testid="navbar-logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={onLogin}
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 text-[#ebebeb] hover:bg-white/5 transition-colors text-sm font-mono"
              data-testid="navbar-login-btn"
            >
              <User className="w-4 h-4" />
              Login
            </button>
          )}
        </div>

        <button 
          className="md:hidden text-white"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-[#191b1d]/95 backdrop-blur-lg border-t border-white/10 p-6">
          <div className="flex flex-col gap-4">
            <a href="#features" className="text-[#ebebeb] hover:text-white transition-colors font-mono">Features</a>
            <a href="#how-it-works" className="text-[#ebebeb] hover:text-white transition-colors font-mono">How It Works</a>
            <a href="#inventory" className="text-[#ebebeb] hover:text-white transition-colors font-mono">Inventory</a>
            <button 
              onClick={() => { onCreateDeal(); setMobileMenuOpen(false); }}
              className="ingenium-button w-full text-center"
            >
              <span>Create a Deal</span>
            </button>
            {isLoggedIn ? (
              <>
                <button
                  onClick={() => { onDashboard(); setMobileMenuOpen(false); }}
                  className="flex items-center justify-center gap-2 py-2 text-[#ebebeb] hover:text-white transition-colors font-mono"
                >
                  <User className="w-4 h-4" />
                  {getDashboardLabel()}
                </button>
                <button
                  onClick={() => { onLogout(); setMobileMenuOpen(false); }}
                  className="flex items-center justify-center gap-2 py-2 text-[#ebebeb]/60 hover:text-white transition-colors font-mono"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </>
            ) : (
              <button
                onClick={() => { onLogin(); setMobileMenuOpen(false); }}
                className="flex items-center justify-center gap-2 py-2 rounded-full border border-white/20 text-[#ebebeb] hover:bg-white/5 transition-colors font-mono"
              >
                <User className="w-4 h-4" />
                Login
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

const HeroSection = ({ onCreateDeal }) => (
  <section className="hero-section hero-gradient pt-24 px-6">
    <div className="max-w-6xl mx-auto text-center relative z-10">
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#ff3737]/10 border border-[#ff3737]/20 mb-8 animate-fade-in-up">
        <Sparkles className="w-4 h-4 text-[#ff3737]" />
        <span className="text-[#ebebeb] text-sm font-mono">Agentic AI-Powered Curation</span>
      </div>
      
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 font-mono animate-fade-in-up delay-100">
        One Platform,{" "}
        <span className="gradient-text">Endless Possibilities</span>
      </h1>
      
      <p className="text-lg text-[#ebebeb]/80 max-w-2xl mx-auto mb-10 animate-fade-in-up delay-200 font-mono">
        The intelligent curation platform that transforms fragmented programmatic workflows into 
        high-performing, transparent campaigns. Built for agencies, brands, and media buyers.
      </p>
      
      <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-300">
        <button 
          onClick={onCreateDeal}
          className="ingenium-button text-lg animate-pulse-glow"
          data-testid="hero-create-deal-btn"
        >
          <span className="flex items-center gap-2">
            Create a Deal
            <ChevronRight className="w-5 h-5" />
          </span>
        </button>
      </div>

      <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 animate-fade-in-up delay-400">
        {[
          { value: "14+", label: "DSP Partners" },
          { value: "3hrs", label: "Deal Delivery" },
          { value: "AI", label: "Powered Curation" },
          { value: "Global", label: "Coverage" }
        ].map((stat, i) => (
          <div key={i} className="text-center">
            <div className="text-3xl font-bold gradient-text font-mono">{stat.value}</div>
            <div className="text-[#ebebeb]/50 text-sm mt-1 font-mono">{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const FeaturesSection = () => (
  <section id="features" className="py-24 px-6 bg-[#191b1d]">
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-16">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-mono">
          Plan Smarter. Activate Faster. Optimize Continuously.
        </h2>
        <p className="text-[#ebebeb]/70 max-w-3xl mx-auto font-mono">
          The only programmatic curation platform that unifies the supply-side ecosystem, 
          connecting planning, activation, and optimization into one intelligent workflow.
        </p>
      </div>

      {/* Three Pillars */}
      <div className="grid md:grid-cols-3 gap-8 mb-20">
        <div className="feature-card rounded-2xl p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-[#ff3737]/15 flex items-center justify-center text-[#ff3737] mx-auto mb-6">
            <Target className="w-8 h-8" />
          </div>
          <h3 className="text-white font-bold text-xl mb-3 font-mono">Plan Smarter</h3>
          <p className="text-[#ebebeb]/60 font-mono text-sm">
            Access a global ecosystem of programmatic supply and audience data. 
            Build targeted strategies with AI-powered tools that eliminate friction and bias from planning.
          </p>
        </div>
        
        <div className="feature-card rounded-2xl p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-[#583aff]/15 flex items-center justify-center text-[#583aff] mx-auto mb-6">
            <Zap className="w-8 h-8" />
          </div>
          <h3 className="text-white font-bold text-xl mb-3 font-mono">Activate Faster</h3>
          <p className="text-[#ebebeb]/60 font-mono text-sm">
            Deploy curated deals through a single access point across multiple SSPs and supply partners. 
            Automated setup, packaging, and distribution customized to your campaign goals.
          </p>
        </div>
        
        <div className="feature-card rounded-2xl p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-[#ff3737]/15 flex items-center justify-center text-[#ff3737] mx-auto mb-6">
            <BarChart3 className="w-8 h-8" />
          </div>
          <h3 className="text-white font-bold text-xl mb-3 font-mono">Optimize Continuously</h3>
          <p className="text-[#ebebeb]/60 font-mono text-sm">
            AI-powered diagnostics combined with expert oversight to refine campaign performance 
            mid-flight and maximize programmatic ROI in real time.
          </p>
        </div>
      </div>

      {/* Feature Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[
          {
            icon: <Shield className="w-6 h-6" />,
            title: "Unbiased by Design",
            description: "Performance without partiality. We never steer campaigns toward a single SSP or dataset, ensuring transparent media buying."
          },
          {
            icon: <Globe className="w-6 h-6" />,
            title: "Unified Access",
            description: "One entry point to programmatic advertising. Maximum reach, full flexibility, and streamlined access across the open web."
          },
          {
            icon: <Sparkles className="w-6 h-6" />,
            title: "AI + Human Expertise",
            description: "AI alone isn't enough. Our team combines human expertise with machine intelligence for smarter decisions."
          },
          {
            icon: <Target className="w-6 h-6" />,
            title: "Data-Driven Curation",
            description: "Each curated deal matches the sites, formats, and creatives most likely to deliver on your goals."
          },
          {
            icon: <BarChart3 className="w-6 h-6" />,
            title: "Real-Time Analytics",
            description: "Continuous bidstream analysis to match inventory with performance objectives in real time."
          },
          {
            icon: <Zap className="w-6 h-6" />,
            title: "24/7 Global Support",
            description: "Round-the-clock coverage across languages and markets, ensuring consistency and local expertise worldwide."
          }
        ].map((feature, i) => (
          <div key={i} className="feature-card rounded-2xl p-6">
            <div className="w-12 h-12 rounded-xl bg-[#ff3737]/15 flex items-center justify-center text-[#ff3737] mb-4">
              {feature.icon}
            </div>
            <h3 className="text-white font-bold text-lg mb-2 font-mono">{feature.title}</h3>
            <p className="text-[#ebebeb]/60 text-sm font-mono">{feature.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const StakeholderSection = () => (
  <section className="py-24 px-6 bg-gradient-to-b from-[#191b1d] to-[#1a1c1f]">
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-16">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-mono">
          Value for Every Stakeholder
        </h2>
        <p className="text-[#ebebeb]/70 max-w-2xl mx-auto font-mono">
          An intelligent curation platform with performance, transparency, and efficiency built in.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        <div className="feature-card rounded-2xl p-8 border-l-4 border-[#ff3737]">
          <h3 className="text-white font-bold text-xl mb-4 font-mono">For Agencies</h3>
          <p className="text-[#ebebeb]/60 font-mono text-sm">
            Delivers precision, transparency, and performance without adding operational overhead. 
            Scale your programmatic operations effortlessly.
          </p>
        </div>
        
        <div className="feature-card rounded-2xl p-8 border-l-4 border-[#583aff]">
          <h3 className="text-white font-bold text-xl mb-4 font-mono">For Brands</h3>
          <p className="text-[#ebebeb]/60 font-mono text-sm">
            Turns audience goals into measurable outcomes with more reach, 
            more relevance, and stronger ROI across all your campaigns.
          </p>
        </div>
        
        <div className="feature-card rounded-2xl p-8 border-l-4 border-[#ff3737]">
          <h3 className="text-white font-bold text-xl mb-4 font-mono">For Media Buyers</h3>
          <p className="text-[#ebebeb]/60 font-mono text-sm">
            Streamlined deal creation and activation. Spend less time on logistics 
            and more time on strategy and optimization.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const HowItWorksSection = () => (
  <section id="how-it-works" className="py-24 px-6 bg-[#191b1d]/80">
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-16">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-mono">
          How Agentic Curation Works
        </h2>
        <p className="text-[#ebebeb]/70 max-w-2xl mx-auto font-mono">
          From request to activation in just a few simple steps.
        </p>
      </div>

      <div className="grid md:grid-cols-4 gap-8">
        {[
          { step: "01", title: "Create Your Deal", desc: "Fill in your requirements: DSP, formats, devices, and geographies." },
          { step: "02", title: "AI Curation", desc: "Our AI agent analyzes inventory and curates the perfect bundle." },
          { step: "03", title: "Deal Delivery", desc: "Receive your custom PMP deal within 3 hours." },
          { step: "04", title: "Activate & Optimize", desc: "Launch your campaign with continuous AI optimization." }
        ].map((item, i) => (
          <div key={i} className="relative">
            <div className="text-6xl font-bold text-[#ff3737]/20 font-mono mb-4">{item.step}</div>
            <h3 className="text-white font-bold text-lg mb-2 font-mono">{item.title}</h3>
            <p className="text-[#ebebeb]/60 text-sm font-mono">{item.desc}</p>
            {i < 3 && (
              <div className="hidden md:block absolute top-8 right-0 w-12 h-0.5 bg-gradient-to-r from-[#ff3737]/40 to-transparent" />
            )}
          </div>
        ))}
      </div>
    </div>
  </section>
);

const PUBLISHER_LOGOS = [
  { name: "The Guardian", logo: "https://thumb.wikimedia.org/wikipedia/commons/thumb/0/0e/The_Guardian.svg/330px-The_Guardian.svg.png" },
  { name: "CNN", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b1/CNN.svg/150px-CNN.svg.png" },
  { name: "BBC", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/41/BBC_Logo_2021.svg/200px-BBC_Logo_2021.svg.png" },
  { name: "Forbes", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0c/Forbes_logo.svg/200px-Forbes_logo.svg.png" },
  { name: "Bloomberg", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/5d/New_Bloomberg_Logo.svg/200px-New_Bloomberg_Logo.svg.png" },
  { name: "ESPN", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2f/ESPN_wordmark.svg/200px-ESPN_wordmark.svg.png" },
  { name: "Reuters", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/8d/Reuters_Logo.svg/200px-Reuters_Logo.svg.png" },
  { name: "Spotify", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/26/Spotify_logo_with_text.svg/200px-Spotify_logo_with_text.svg.png" }
];

const PLATFORM_LOGOS = [
  { name: "Google DV360", shortName: "DV360" },
  { name: "The Trade Desk", shortName: "TTD" },
  { name: "Yahoo", shortName: "Yahoo" },
  { name: "Quantcast", shortName: "Quantcast" },
  { name: "Adform", shortName: "Adform" },
  { name: "Amazon", shortName: "Amazon" },
  { name: "Adobe", shortName: "Adobe" }
];

const InventorySection = () => (
  <section id="inventory" className="py-24 px-6 bg-[#191b1d]">
    <div className="max-w-6xl mx-auto">
      {/* Premium Inventory */}
      <div className="text-center mb-12">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-mono">
          Premium Inventory
        </h2>
        <p className="text-[#ebebeb]/70 max-w-2xl mx-auto font-mono">
          Get access to premium media across your chosen markets and formats. 
          We work with trusted publishers worldwide, securing high-quality, brand-safe placements.
        </p>
      </div>

      {/* Publisher Logos */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-6 mb-20">
        {PUBLISHER_LOGOS.map((publisher, i) => (
          <div 
            key={i} 
            className="feature-card rounded-xl p-4 flex items-center justify-center h-20 grayscale hover:grayscale-0 transition-all duration-300"
          >
            <img 
              src={publisher.logo} 
              alt={publisher.name}
              className="max-h-8 max-w-full object-contain brightness-0 invert opacity-60 hover:opacity-100 transition-opacity"
            />
          </div>
        ))}
      </div>

      {/* Platforms Section */}
      <div className="text-center mb-10">
        <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 font-mono">
          Platforms
        </h3>
        <p className="text-[#ebebeb]/70 max-w-xl mx-auto font-mono">
          Supported by all major demand side platforms.
        </p>
      </div>

      <div className="flex flex-wrap justify-center gap-4">
        {PLATFORM_LOGOS.map((platform, i) => (
          <div 
            key={i} 
            className="feature-card rounded-xl px-8 py-4 flex items-center justify-center hover:border-[#ff3737]/30 transition-all duration-300"
          >
            <span className="text-xl font-bold text-[#ebebeb]/70 hover:text-white transition-colors font-mono tracking-tight">
              {platform.shortName}
            </span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const CTASection = ({ onCreateDeal }) => (
  <section className="py-24 px-6 bg-[#191b1d]">
    <div className="max-w-4xl mx-auto">
      <div className="feature-card rounded-3xl p-12 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#ff3737]/5 to-[#ff6b6b]/5" />
        <div className="relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-mono">
            Ready to Create Your Curated Deal?
          </h2>
          <p className="text-[#ebebeb]/60 max-w-xl mx-auto mb-8 font-mono">
            Whether you use DV360, The Trade Desk, or another platform, we deliver a custom-curated deal to match. 
            All formats, all markets - all curated for results.
          </p>
          <button 
            onClick={onCreateDeal}
            className="ingenium-button text-lg"
            data-testid="cta-create-deal-btn"
          >
            <span className="flex items-center gap-2">
              Create a Deal
              <ChevronRight className="w-5 h-5" />
            </span>
          </button>
        </div>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="py-12 px-6 border-t border-white/10 bg-[#191b1d]">
    <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <img src={INGENIUM_LOGO} alt="Ingenium Digital" className="h-6" />
        <span className="text-[#ebebeb]/60 text-sm font-mono">© 2026 Ingenium Digital. All rights reserved.</span>
      </div>
      <div className="flex items-center gap-6">
        <a href="/?login=true" className="text-[#ebebeb]/50 hover:text-[#ebebeb] text-sm transition-colors font-mono">
          Client Portal
        </a>
        <a href="/admin" className="text-[#ebebeb]/50 hover:text-[#ebebeb] text-sm transition-colors font-mono">
          Admin Portal
        </a>
      </div>
    </div>
  </footer>
);

const MultiSelect = ({ label, options, selected, onChange, testId }) => (
  <div className="space-y-2">
    <Label className="text-[#ebebeb]">{label}</Label>
    <div className="grid grid-cols-2 gap-2">
      {options.map((option) => (
        <label
          key={option}
          className={`checkbox-item rounded-lg p-3 flex items-center gap-3 cursor-pointer ${
            selected.includes(option) ? 'selected' : ''
          }`}
        >
          <Checkbox
            checked={selected.includes(option)}
            onCheckedChange={(checked) => {
              if (checked) {
                onChange([...selected, option]);
              } else {
                onChange(selected.filter(item => item !== option));
              }
            }}
            data-testid={`${testId}-${option.toLowerCase().replace(/\s+/g, '-')}`}
          />
          <span className="text-sm text-[#ebebeb]">{option}</span>
        </label>
      ))}
    </div>
  </div>
);

const CreateDealModal = ({ open, onClose }) => {
  // Check if user is logged in (either client or admin)
  const clientToken = localStorage.getItem("clientToken");
  const adminToken = localStorage.getItem("adminToken");
  const isLoggedIn = !!(clientToken || adminToken);
  
  // Get user details based on login type
  const getUserDetails = () => {
    if (clientToken) {
      return {
        token: clientToken,
        name: localStorage.getItem("clientName") || "",
        email: localStorage.getItem("clientEmail") || "",
        company: localStorage.getItem("clientCompany") || "",
        isClient: true
      };
    }
    if (adminToken) {
      return {
        token: adminToken,
        name: localStorage.getItem("adminName") || "",
        email: localStorage.getItem("adminEmail") || "",
        company: "",
        isClient: false
      };
    }
    return { token: null, name: "", email: "", company: "", isClient: false };
  };
  
  const userDetails = getUserDetails();
  
  const [formData, setFormData] = useState({
    name: userDetails.name,
    email: userDetails.email,
    company: userDetails.company,
    dsp: "",
    dsp_seat_number: "",
    media_type: [],
    device: [],
    environment: [],
    geography: [],
    custom_request: "",
    primary_kpi: "",
    primary_kpi_value: "",
    primary_kpi_currency: "USD"
  });
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submittedDealId, setSubmittedDealId] = useState(null);
  const [accountCreated, setAccountCreated] = useState(false);
  const [setPasswordToken, setSetPasswordToken] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [settingPassword, setSettingPassword] = useState(false);
  const [passwordSet, setPasswordSet] = useState(false);
  const [step, setStep] = useState(1);
  const [geoSearch, setGeoSearch] = useState("");

  const filteredCountries = COUNTRIES.filter(country => 
    country.toLowerCase().includes(geoSearch.toLowerCase())
  );

  // Auto-fill form with user data when modal opens
  useEffect(() => {
    if (open) {
      const details = getUserDetails();
      if (details.token) {
        setFormData(prev => ({
          ...prev,
          name: details.name || prev.name,
          email: details.email || prev.email,
          company: details.company || prev.company
        }));
      }
    }
  }, [open]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAIAssist = async () => {
    if (!formData.custom_request.trim()) {
      toast.error("Please enter a question or request first");
      return;
    }
    
    setAiLoading(true);
    try {
      const context = `Current selections - DSP: ${formData.dsp || 'Not selected'}, Media Types: ${formData.media_type.join(', ') || 'None'}, Devices: ${formData.device.join(', ') || 'None'}, Geographies: ${formData.geography.join(', ') || 'None'}`;
      
      const response = await axios.post(`${API}/ai/assist`, {
        message: formData.custom_request,
        context
      });
      setAiResponse(response.data.response);
    } catch (error) {
      toast.error("Failed to get AI assistance. Please try again.");
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.company || !formData.dsp || !formData.dsp_seat_number) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (formData.media_type.length === 0 || formData.device.length === 0 || formData.environment.length === 0 || formData.geography.length === 0) {
      toast.error("Please select at least one option for each category");
      return;
    }

    setLoading(true);
    try {
      // Include auth token if user is logged in (for automatic deal linking)
      const headers = {};
      const token = localStorage.getItem("clientToken") || localStorage.getItem("adminToken");
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await axios.post(`${API}/deals`, formData, { headers });
      setSubmittedDealId(response.data.id);
      if (response.data.account_created) {
        setAccountCreated(true);
        setSetPasswordToken(response.data.set_password_token);
      }
      setSubmitted(true);
      toast.success("Deal request submitted successfully!");
      if (formLogId) { axios.patch(`${API}/form-logs/${formLogId}/submitted`).catch(() => {}); }
    } catch (error) {
      toast.error("Failed to submit deal request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const [formLogId, setFormLogId] = useState(null);
  const loggedRef = useRef(false);

  const logFormData = async (trigger) => {
    if (loggedRef.current) return;
    const hasData = formData.name || formData.email || formData.company || formData.dsp || formData.dsp_seat_number;
    if (!hasData) return;
    loggedRef.current = true;
    try {
      const res = await axios.post(`${API}/form-logs`, {
        name: formData.name, email: formData.email, company: formData.company,
        dsp: formData.dsp, dsp_seat_number: formData.dsp_seat_number, trigger
      });
      if (res.data.id) setFormLogId(res.data.id);
    } catch { /* silent */ }
  };

  const resetForm = () => {
    setFormData({
      name: "", email: "", company: "", dsp: "", dsp_seat_number: "",
      media_type: [], device: [], environment: [], geography: [], custom_request: "",
      primary_kpi: "", primary_kpi_value: "", primary_kpi_currency: "USD"
    });
    setAiResponse("");
    setSubmitted(false);
    setSubmittedDealId(null);
    setAccountCreated(false);
    setSetPasswordToken(null);
    setNewPassword("");
    setPasswordSet(false);
    setStep(1);
    loggedRef.current = false;
    setFormLogId(null);
    onClose();
  };

  if (submitted) {
    const isUserLoggedIn = !!(localStorage.getItem("clientToken") || localStorage.getItem("adminToken"));
    const isClientLoggedIn = !!localStorage.getItem("clientToken");
    
    const handleSetPassword = async () => {
      if (!newPassword || newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
      setSettingPassword(true);
      try {
        const response = await axios.post(`${API}/auth/set-password`, { token: setPasswordToken, password: newPassword });
        const { token, role, name: rName, user_id, company: rCompany } = response.data;
        localStorage.setItem("clientToken", token);
        localStorage.setItem("clientName", rName);
        localStorage.setItem("clientEmail", formData.email);
        localStorage.setItem("clientCompany", rCompany || "");
        localStorage.setItem("clientUserId", user_id || "");
        setPasswordSet(true);
        toast.success("Password set! You're now logged in.");
      } catch { toast.error("Failed to set password. Please try again."); }
      finally { setSettingPassword(false); }
    };
    
    return (
      <Dialog open={open} onOpenChange={resetForm}>
        <DialogContent className="bg-[#191b1d] border-[#ff3737]/20 max-w-md">
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-green-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4 font-mono">Request Received!</h3>
            <p className="text-[#ebebeb]/70 mb-6 font-mono">
              Your deal request has been successfully submitted. Our team will review your requirements and 
              <span className="text-[#ff3737] font-medium"> deals will be sent to your seat within 3 hours</span>.
            </p>
            
            {/* Auto-created account — set password prompt */}
            {accountCreated && !passwordSet ? (
              <div className="bg-white/5 rounded-xl p-4 mb-6 text-left" data-testid="set-password-section">
                <p className="text-[#ebebeb] text-sm font-mono mb-3">
                  We've created an account for <span className="text-[#ff3737]">{formData.email}</span>. Set a password to log in and track your deals.
                </p>
                <div className="flex gap-2">
                  <Input 
                    type="password" placeholder="Set a password (min 6 chars)" 
                    value={newPassword} onChange={(e) => setNewPassword(e.target.value)} 
                    className="form-input flex-1" data-testid="set-password-input"
                    onKeyDown={(e) => e.key === 'Enter' && handleSetPassword()}
                  />
                  <button 
                    onClick={handleSetPassword} disabled={settingPassword}
                    className="ingenium-button px-4 whitespace-nowrap"
                    data-testid="set-password-btn"
                  >
                    <span>{settingPassword ? "..." : "Set Password"}</span>
                  </button>
                </div>
              </div>
            ) : passwordSet || isUserLoggedIn ? (
              <div className="bg-white/5 rounded-xl p-4 mb-6 text-left">
                <p className="text-[#ebebeb] text-sm font-mono mb-3">
                  {passwordSet 
                    ? "Your account is ready! You can now track your deal status in your dashboard."
                    : isClientLoggedIn 
                      ? "This deal has been added to your account. You can track its status in your dashboard."
                      : "Deal submitted successfully. You can view it in the Admin Dashboard."
                  }
                </p>
                <a 
                  href={passwordSet || isClientLoggedIn ? "/portal/dashboard" : "/admin/dashboard"}
                  className="block w-full py-2 px-4 rounded-full border border-[#ff3737]/30 text-[#ff3737] hover:bg-[#ff3737]/10 transition-colors font-mono text-sm text-center"
                >
                  View My Deals
                </a>
              </div>
            ) : (
              <div className="bg-white/5 rounded-xl p-4 mb-6 text-left">
                <p className="text-[#ebebeb] text-sm font-mono mb-3">
                  Want to track your deal status? Create an account or login to save this deal.
                </p>
                <a 
                  href="/?register=true"
                  className="block w-full py-2 px-4 rounded-full border border-[#ff3737]/30 text-[#ff3737] hover:bg-[#ff3737]/10 transition-colors font-mono text-sm text-center"
                >
                  Login / Register
                </a>
              </div>
            )}
            
            <button onClick={resetForm} className="ingenium-button w-full" data-testid="success-close-btn">
              <span>Close</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) { logFormData("modal_close"); resetForm(); } }}>
      <DialogContent className="bg-[#191b1d] border-[#ff3737]/20 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white font-mono">
            Create a Deal
          </DialogTitle>
          <DialogDescription className="text-[#ebebeb]/70 font-mono">
            Fill in your requirements and our AI will help curate the perfect PMP deal for you.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 mb-6">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`flex-1 h-1 rounded-full transition-colors ${
                step >= s ? 'bg-gradient-to-r from-[#ff3737] to-[#ff6b6b]' : 'bg-white/10'
              }`}
            />
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4">
            {/* Show logged-in indicator if user is authenticated */}
            {isLoggedIn && (
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-400" />
                <span className="text-green-400 text-sm font-mono">
                  Logged in as {userDetails.name || userDetails.email}
                </span>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#ebebeb]">Name *</Label>
                <Input
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`form-input ${isLoggedIn && userDetails.name ? 'bg-white/5' : ''}`}
                  data-testid="input-name"
                  readOnly={isLoggedIn && !!userDetails.name}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#ebebeb]">Email *</Label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`form-input ${isLoggedIn && userDetails.email ? 'bg-white/5' : ''}`}
                  data-testid="input-email"
                  readOnly={isLoggedIn && !!userDetails.email}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#ebebeb]">Company *</Label>
              <Input
                placeholder="Company name"
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                className={`form-input ${isLoggedIn && userDetails.company ? 'bg-white/5' : ''}`}
                data-testid="input-company"
                readOnly={isLoggedIn && !!userDetails.company}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#ebebeb]">DSP *</Label>
                <select
                  value={formData.dsp}
                  onChange={(e) => handleInputChange('dsp', e.target.value)}
                  className="flex h-9 w-full items-center justify-between rounded-md border border-[#ff3737]/15 bg-[#191b1d] px-3 py-2 text-sm text-[#ebebeb] shadow-sm focus:outline-none focus:ring-1 focus:ring-[#ff3737] font-mono"
                  data-testid="select-dsp"
                >
                  <option value="" disabled>Select DSP</option>
                  {DSP_OPTIONS.map((dsp) => (
                    <option key={dsp} value={dsp} className="bg-[#191b1d] text-[#ebebeb]">
                      {dsp}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-[#ebebeb]">DSP Seat Number *</Label>
                <Input
                  placeholder="Enter seat number"
                  value={formData.dsp_seat_number}
                  onChange={(e) => handleInputChange('dsp_seat_number', e.target.value)}
                  className="form-input"
                  data-testid="input-seat-number"
                />
              </div>
            </div>
            <button 
              onClick={() => { logFormData("step1_next"); setStep(2); }} 
              className="ingenium-button w-full text-center"
              disabled={!formData.name || !formData.email || !formData.company || !formData.dsp || !formData.dsp_seat_number}
              data-testid="step1-next-btn"
            >
              <span className="flex items-center justify-center gap-2">
                Next: Deal Configuration
                <ChevronRight className="w-4 h-4" />
              </span>
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <MultiSelect
              label="Media Type *"
              options={MEDIA_TYPES}
              selected={formData.media_type}
              onChange={(v) => handleInputChange('media_type', v)}
              testId="media-type"
            />
            <MultiSelect
              label="Device *"
              options={DEVICES}
              selected={formData.device}
              onChange={(v) => handleInputChange('device', v)}
              testId="device"
            />
            <MultiSelect
              label="Environment *"
              options={ENVIRONMENTS}
              selected={formData.environment}
              onChange={(v) => handleInputChange('environment', v)}
              testId="environment"
            />
            <div className="flex gap-3">
              <button 
                onClick={() => setStep(1)} 
                className="flex-1 py-3 px-6 rounded-full border border-white/20 text-[#ebebeb] hover:bg-white/5 transition-colors font-mono"
                data-testid="step2-back-btn"
              >
                Back
              </button>
              <button 
                onClick={() => setStep(3)} 
                className="ingenium-button flex-1 text-center disabled:opacity-50"
                disabled={formData.media_type.length === 0 || formData.device.length === 0 || formData.environment.length === 0}
                data-testid="step2-next-btn"
              >
                <span className="flex items-center justify-center gap-2">
                  Next: Geography & Custom Request
                  <ChevronRight className="w-4 h-4" />
                </span>
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-[#ebebeb]">Geography *</Label>
                <div className="flex items-center gap-3">
                  {formData.geography.length > 0 && (
                    <span className="text-xs text-[#ff3737]">{formData.geography.length} selected</span>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      if (formData.geography.length === COUNTRIES.length) {
                        handleInputChange('geography', []);
                      } else {
                        handleInputChange('geography', [...COUNTRIES]);
                      }
                    }}
                    className="text-xs h-7 px-3 rounded-full border border-[#ff3737]/30 text-[#ff3737] hover:bg-[#ff3737]/10 font-mono transition-colors"
                    data-testid="geo-select-all-btn"
                  >
                    {formData.geography.length === COUNTRIES.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#ebebeb]/40" />
                <Input
                  placeholder="Search countries..."
                  value={geoSearch}
                  onChange={(e) => setGeoSearch(e.target.value)}
                  className="form-input pl-10"
                  data-testid="geo-search-input"
                />
              </div>
              <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto p-2 bg-[#191b1d]/80 rounded-lg border border-white/10">
                {filteredCountries.length === 0 ? (
                  <p className="col-span-3 text-center text-[#ebebeb]/50 py-4 text-sm font-mono">No countries found</p>
                ) : (
                  filteredCountries.map((country) => (
                    <label
                      key={country}
                      className={`checkbox-item rounded-lg p-2 flex items-center gap-2 cursor-pointer text-xs ${
                        formData.geography.includes(country) ? 'selected' : ''
                      }`}
                    >
                      <Checkbox
                        checked={formData.geography.includes(country)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            handleInputChange('geography', [...formData.geography, country]);
                          } else {
                            handleInputChange('geography', formData.geography.filter(g => g !== country));
                          }
                        }}
                        data-testid={`geo-${country.toLowerCase().replace(/\s+/g, '-')}`}
                      />
                      <span className="text-[#ebebeb]">{country}</span>
                    </label>
                  ))
                )}
              </div>
            </div>

            {/* Primary KPI (Optional) */}
            <div className="space-y-3">
              <Label className="text-[#ebebeb]">Primary KPI (Optional)</Label>
              <div className="flex gap-3">
                <div className="flex-1">
                  <Select value={formData.primary_kpi} onValueChange={(v) => { handleInputChange('primary_kpi', v); handleInputChange('primary_kpi_value', ''); }}>
                    <SelectTrigger className="form-input" data-testid="primary-kpi-select">
                      <SelectValue placeholder="Select KPI" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1e2124] border-white/10">
                      {["CPM", "CPC", "CTR", "CPCV", "Viewability", "Attention", "Completion Rate"].map(kpi => (
                        <SelectItem key={kpi} value={kpi} className="text-[#ebebeb] focus:bg-white/10 focus:text-white">{kpi}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {formData.primary_kpi && (
                  <>
                    <div className="flex-1">
                      <Input
                        type="number"
                        step="any"
                        placeholder={["CPM","CPC","CPCV"].includes(formData.primary_kpi) ? "Value (e.g. 5.00)" : "% (e.g. 75)"}
                        value={formData.primary_kpi_value}
                        onChange={(e) => handleInputChange('primary_kpi_value', e.target.value)}
                        className="form-input"
                        data-testid="primary-kpi-value"
                      />
                      <p className="text-[#ebebeb]/40 text-xs mt-1">
                        {["CPM","CPC","CPCV"].includes(formData.primary_kpi) ? "Currency value" : "Percentage value"}
                      </p>
                    </div>
                    {["CPM","CPC","CPCV"].includes(formData.primary_kpi) && (
                      <div className="w-24">
                        <Select value={formData.primary_kpi_currency} onValueChange={(v) => handleInputChange('primary_kpi_currency', v)}>
                          <SelectTrigger className="form-input" data-testid="primary-kpi-currency">
                            <SelectValue placeholder="USD" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1e2124] border-white/10">
                            {["USD","EUR","GBP","HKD","SGD","AUD","JPY","CNY","CAD","CHF"].map(c => (
                              <SelectItem key={c} value={c} className="text-[#ebebeb] focus:bg-white/10 focus:text-white">{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="ai-chat-container rounded-xl p-4 space-y-4">
              <div className="flex items-center gap-2 text-[#ff3737]">
                <Bot className="w-5 h-5" />
                <span className="font-bold font-mono">AI Deal Assistant</span>
              </div>
              <div className="space-y-2">
                <Label className="text-[#ebebeb]">Custom Request or Questions</Label>
                <Textarea
                  placeholder="Describe any specific requirements, ask questions about targeting options, or request guidance on your deal setup..."
                  value={formData.custom_request}
                  onChange={(e) => handleInputChange('custom_request', e.target.value)}
                  className="form-input min-h-24"
                  data-testid="textarea-custom-request"
                />
              </div>
              <button 
                onClick={handleAIAssist}
                disabled={aiLoading || !formData.custom_request.trim()}
                className="py-2 px-4 rounded-full border border-[#ff3737]/30 text-[#ff3737] hover:bg-[#ff3737]/10 font-mono text-sm transition-colors disabled:opacity-50 flex items-center gap-2"
                data-testid="ai-assist-btn"
              >
                {aiLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Getting AI Assistance...</>
                ) : (
                  <><Sparkles className="w-4 h-4" /> Get AI Assistance</>
                )}
              </button>
              {aiResponse && (
                <div className="ai-message rounded-lg p-4 mt-4">
                  <p className="text-[#ebebeb] text-sm whitespace-pre-wrap font-mono">{aiResponse}</p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button 
                onClick={() => setStep(2)} 
                className="flex-1 py-3 px-6 rounded-full border border-white/20 text-[#ebebeb] hover:bg-white/5 transition-colors font-mono"
                data-testid="step3-back-btn"
              >
                Back
              </button>
              <button 
                onClick={handleSubmit}
                disabled={loading || formData.geography.length === 0}
                className="ingenium-button flex-1 text-center disabled:opacity-50"
                data-testid="submit-deal-btn"
              >
                <span className="flex items-center justify-center gap-2">
                  {loading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
                  ) : (
                    <><Send className="w-4 h-4" /> Submit Deal Request</>
                  )}
                </span>
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Unified Auth Modal (Login + Register + Forgot Password)
const AuthModal = ({ open, onClose, onLoginSuccess, initialView = "login" }) => {
  const [view, setView] = useState(initialView);
  const [loading, setLoading] = useState(false);

  // Login / Register fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");

  useEffect(() => {
    if (open) setView(initialView);
  }, [open, initialView]);

  const resetAll = () => {
    setEmail(""); setPassword(""); setName(""); setCompany("");
    setView("login");
  };

  const handleClose = () => { resetAll(); onClose(); };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/portal/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Please enter email and password"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { token, role, name: rName, user_id, company: rCompany } = response.data;
      if (role === "client") {
        localStorage.setItem("clientToken", token);
        localStorage.setItem("clientName", rName);
        localStorage.setItem("clientEmail", email);
        localStorage.setItem("clientCompany", rCompany || "");
        localStorage.setItem("clientUserId", user_id || "");
        localStorage.removeItem("adminToken"); localStorage.removeItem("adminRole");
        localStorage.removeItem("adminName"); localStorage.removeItem("adminUserId");
      } else {
        localStorage.setItem("adminToken", token);
        localStorage.setItem("adminRole", role);
        localStorage.setItem("adminName", rName);
        localStorage.setItem("adminUserId", user_id || "");
        localStorage.removeItem("clientToken"); localStorage.removeItem("clientName");
        localStorage.removeItem("clientEmail"); localStorage.removeItem("clientCompany");
        localStorage.removeItem("clientUserId");
      }
      toast.success(`Welcome back, ${rName}!`);
      onLoginSuccess(role, rName);
      handleClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid credentials");
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) { toast.error("Please fill in all required fields"); return; }
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, { email, password, name, company: company || null });
      const { token, role, name: rName, user_id, company: rCompany } = response.data;
      localStorage.setItem("clientToken", token);
      localStorage.setItem("clientName", rName);
      localStorage.setItem("clientEmail", email);
      localStorage.setItem("clientCompany", rCompany || "");
      localStorage.setItem("clientUserId", user_id || "");
      toast.success(`Welcome, ${rName}!`);
      onLoginSuccess(role, rName);
      handleClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }}>
      <DialogContent className="bg-[#191b1d] border-[#ff3737]/20 max-w-md">
        {view === "email_login" ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-white font-mono text-center">Sign In</DialogTitle>
              <DialogDescription className="text-[#ebebeb]/60 font-mono text-center">Enter your email and password</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleLogin} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-[#ebebeb]">Email</Label>
                <Input type="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} className="form-input" data-testid="login-email" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#ebebeb]">Password</Label>
                <Input type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} className="form-input" data-testid="login-password" />
              </div>
              <button type="submit" disabled={loading} className="ingenium-button w-full text-center" data-testid="login-submit">
                <span className="flex items-center justify-center gap-2">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {loading ? "Signing in..." : "Sign In"}
                </span>
              </button>
            </form>
            <div className="flex items-center justify-between mt-4">
              <button onClick={() => setView("login")} className="text-[#ebebeb]/40 hover:text-white text-sm font-mono" data-testid="back-to-options">
                Back
              </button>
              <button onClick={() => setView("register")} className="text-[#ff3737] hover:text-[#ff6b6b] text-sm font-mono" data-testid="switch-to-register">
                Create Account
              </button>
            </div>
          </>
        ) : view === "register" ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-white font-mono text-center">Create Account</DialogTitle>
              <DialogDescription className="text-[#ebebeb]/60 font-mono text-center">Sign up to track your deals</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleRegister} className="space-y-3 mt-4">
              <div className="space-y-1.5">
                <Label className="text-[#ebebeb]">Full Name *</Label>
                <Input type="text" placeholder="John Doe" value={name} onChange={(e) => setName(e.target.value)} className="form-input" data-testid="register-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#ebebeb]">Email *</Label>
                <Input type="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} className="form-input" data-testid="register-email" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#ebebeb]">Password *</Label>
                <Input type="password" placeholder="Min 6 characters" value={password} onChange={(e) => setPassword(e.target.value)} className="form-input" data-testid="register-password" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#ebebeb]">Company <span className="text-[#ebebeb]/40">(optional)</span></Label>
                <Input type="text" placeholder="Your company" value={company} onChange={(e) => setCompany(e.target.value)} className="form-input" data-testid="register-company" />
              </div>
              <button type="submit" disabled={loading} className="ingenium-button w-full text-center" data-testid="register-submit">
                <span className="flex items-center justify-center gap-2">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <User className="w-4 h-4" />}
                  {loading ? "Creating..." : "Create Account"}
                </span>
              </button>
            </form>
            <div className="mt-4 text-center">
              <button onClick={() => setView("login")} className="text-[#ebebeb]/40 hover:text-white text-sm font-mono" data-testid="switch-to-login">
                Already have an account? <span className="text-[#ff3737]">Sign In</span>
              </button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-white font-mono text-center">Welcome</DialogTitle>
              <DialogDescription className="text-[#ebebeb]/60 font-mono text-center">Sign in to access your account</DialogDescription>
            </DialogHeader>
            <div className="space-y-5 mt-6">
              <button
                onClick={handleGoogleLogin}
                className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-full bg-white text-[#191b1d] font-medium hover:bg-gray-100 transition-colors"
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10"></div></div>
                <div className="relative flex justify-center text-xs"><span className="bg-[#191b1d] px-3 text-[#ebebeb]/40">or</span></div>
              </div>

              <button
                onClick={() => setView("email_login")}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-full border border-white/10 text-[#ebebeb]/60 hover:text-white hover:bg-white/5 transition-colors text-sm font-mono"
                data-testid="email-login-toggle"
              >
                <Send className="w-4 h-4" />
                Login with Email
              </button>

              <div className="text-center">
                <button onClick={() => setView("register")} className="text-[#ebebeb]/40 hover:text-white text-xs font-mono" data-testid="go-to-register">
                  Don't have an account? <span className="text-[#ff3737]">Create one</span>
                </button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [authInitialView, setAuthInitialView] = useState("login");
  
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState("");
  const [userRole, setUserRole] = useState("");
  
  // Check for existing auth on mount
  useEffect(() => {
    const adminToken = localStorage.getItem("adminToken");
    const clientToken = localStorage.getItem("clientToken");
    
    if (adminToken) {
      setIsLoggedIn(true);
      setUserName(localStorage.getItem("adminName") || "Admin");
      setUserRole(localStorage.getItem("adminRole") || "admin");
    } else if (clientToken) {
      setIsLoggedIn(true);
      setUserName(localStorage.getItem("clientName") || "User");
      setUserRole("client");
    }
  }, []);
  
  // Check for ?create=true or ?login=true URL parameter to auto-open modals
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('create') === 'true') {
      setModalOpen(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (urlParams.get('login') === 'true') {
      setAuthInitialView("login");
      setLoginModalOpen(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (urlParams.get('register') === 'true') {
      setAuthInitialView("register");
      setLoginModalOpen(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleLoginSuccess = (role, name) => {
    setIsLoggedIn(true);
    setUserName(name);
    setUserRole(role);
    // Auto-redirect to appropriate dashboard after login
    if (role === "client") {
      navigate("/portal/dashboard");
    } else {
      navigate("/admin/dashboard");
    }
  };

  const handleLogout = () => {
    // Clear all auth data
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminRole");
    localStorage.removeItem("adminName");
    localStorage.removeItem("adminUserId");
    localStorage.removeItem("clientToken");
    localStorage.removeItem("clientName");
    localStorage.removeItem("clientEmail");
    localStorage.removeItem("clientCompany");
    localStorage.removeItem("clientUserId");
    
    setIsLoggedIn(false);
    setUserName("");
    setUserRole("");
    toast.success("Logged out successfully");
  };

  const handleDashboard = () => {
    if (userRole === "client") {
      navigate("/portal/dashboard");
    } else {
      navigate("/admin/dashboard");
    }
  };

  return (
    <div className="min-h-screen bg-[#191b1d]" data-testid="landing-page">
      <Navbar 
        onCreateDeal={() => setModalOpen(true)} 
        onLogin={() => { setAuthInitialView("login"); setLoginModalOpen(true); }}
        isLoggedIn={isLoggedIn}
        userName={userName}
        userRole={userRole}
        onLogout={handleLogout}
        onDashboard={handleDashboard}
      />
      <HeroSection onCreateDeal={() => setModalOpen(true)} />
      <FeaturesSection />
      <StakeholderSection />
      <HowItWorksSection />
      <InventorySection />
      <CTASection onCreateDeal={() => setModalOpen(true)} />
      <Footer />
      <CreateDealModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <AuthModal 
        open={loginModalOpen} 
        onClose={() => setLoginModalOpen(false)} 
        onLoginSuccess={handleLoginSuccess}
        initialView={authInitialView}
      />
    </div>
  );
};

export default LandingPage;
